import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDippyStore } from '@/stores/dippy-store';
import type { DippyConfig } from './useDippySettings';
import api from '@/lib/api';

const TIPS: Record<string, string> = {
  '/admin/agent': "I monitor your store 24/7. Check the Status tab to see what needs attention.",
  '/admin/agent/chat': "Ask me anything about your store \u2014 orders, inventory, pricing.",
  '/admin/agent/config': "Configure which AI models I use for different task types.",
  '/admin/agent/tasks': "Tasks show everything I've done. Click any to see the execution log.",
  '/admin/agent/approvals': "Some actions need your approval. Review them here.",
  '/admin/agent/notifications': "I'll alert you when something needs your attention.",
};

export function useDippyAlerts(config: DippyConfig) {
  const location = useLocation();
  const { enqueueBubble, setExpression, setBadgeCount } = useDippyStore();
  const seenAlertIdsRef = useRef<Set<string>>(new Set());
  const prevUnreadRef = useRef<number | null>(null);
  const tipsShownRef = useRef<Set<string>>(
    new Set(JSON.parse(sessionStorage.getItem('dippy-tips-shown') || '[]'))
  );

  // Contextual tips
  useEffect(() => {
    if (!config.show_tips) return;

    const path = location.pathname;
    // Match exact path or the base for sub-routes
    const tipKey = Object.keys(TIPS).find((k) =>
      k === path || (k !== '/admin/agent' && path.startsWith(k))
    ) ?? (path.startsWith('/admin/agent') && path.split('/').length <= 4 ? '/admin/agent' : null);

    if (!tipKey || tipsShownRef.current.has(tipKey)) return;

    // Delay tip to not interrupt page load
    const timer = setTimeout(() => {
      tipsShownRef.current.add(tipKey);
      sessionStorage.setItem('dippy-tips-shown', JSON.stringify([...tipsShownRef.current]));
      enqueueBubble({
        type: 'tip',
        title: 'Tip',
        message: TIPS[tipKey],
        autoMs: 10000,
      });
    }, 2000);

    return () => clearTimeout(timer);
  }, [location.pathname, config.show_tips]);

  // Proactive alerts
  useEffect(() => {
    if (!config.show_alerts) return;

    const pollMs = (config.poll_interval_seconds ?? 30) * 1000;

    async function checkAlerts() {
      try {
        // Check dashboard needs_attention
        const dashboard = await api.get<{
          needs_attention: {
            escalated_tasks: Array<{ id: string; task_type: string }>;
            failed_orders: Array<{ id: string; order_number: string | null }>;
          };
        }>('/admin/agent/dashboard');

        const attention = dashboard.needs_attention ?? { escalated_tasks: [], failed_orders: [] };
        let newAlerts = 0;

        for (const task of attention.escalated_tasks) {
          if (!seenAlertIdsRef.current.has(task.id)) {
            seenAlertIdsRef.current.add(task.id);
            newAlerts++;
            if (config.alert_style === 'speech_bubble') {
              enqueueBubble({
                type: 'alert',
                title: 'Escalated Task',
                message: `Task "${task.task_type}" needs your attention.`,
                action: { label: 'View task', href: `/admin/agent/tasks/${task.id}` },
              });
            }
          }
        }

        for (const order of attention.failed_orders) {
          if (!seenAlertIdsRef.current.has(order.id)) {
            seenAlertIdsRef.current.add(order.id);
            newAlerts++;
            if (config.alert_style === 'speech_bubble') {
              enqueueBubble({
                type: 'alert',
                title: 'Failed Order',
                message: order.order_number
                  ? `Order #${order.order_number} failed to forward.`
                  : 'A supplier order failed.',
                action: { label: 'View orders', href: '/admin/supplier-orders' },
              });
            }
          }
        }

        // Check unread notifications
        const { count } = await api.get<{ count: number }>('/admin/agent/notifications/count');
        setBadgeCount(count);

        if (prevUnreadRef.current !== null && count > prevUnreadRef.current && config.alert_style === 'speech_bubble') {
          enqueueBubble({
            type: 'alert',
            title: 'New Notifications',
            message: `You have ${count} unread notification${count !== 1 ? 's' : ''}.`,
            action: { label: 'View', href: '/admin/agent/notifications' },
          });
        }
        prevUnreadRef.current = count;

        // Update expression
        if (newAlerts > 0) {
          setExpression('alert');
          setTimeout(() => setExpression('idle'), 2000);
        }
      } catch {
        // silently ignore
      }
    }

    checkAlerts();
    const interval = setInterval(checkAlerts, pollMs);
    return () => clearInterval(interval);
  }, [config.show_alerts, config.alert_style, config.poll_interval_seconds]);
}
